import * as React from "react";
import {useContext} from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {TradingBacktestResult} from "../utils/typesTrading";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {AuthContext} from "./AuthContext";
import {SubscriptionTier} from "../utils/types";

const TRAILING_PERIODS = ['1M', '3M', '6M', '1Y', '3Y', '5Y', '10Y'];

const COLORS = {
    excellent: 'rgba(56, 142, 60, 0.25)',
    good:      'rgba(104, 159, 56, 0.25)',
    fair:      'rgba(251, 192, 45, 0.25)',
    poor:      'rgba(211, 47, 47, 0.20)',
} as const;

// Higher value = better (e.g. Sharpe, Return, CAGR)
const getColorHigher = (v: number | null, t1: number, t2: number, t3: number): string | undefined => {
    if (v == null) return undefined;
    if (v >= t1) return COLORS.excellent;
    if (v >= t2) return COLORS.good;
    if (v >= t3) return COLORS.fair;
    return COLORS.poor;
};

// Lower value = better (e.g. Volatility, Kurtosis)
const getColorLower = (v: number | null, t1: number, t2: number, t3: number): string | undefined => {
    if (v == null) return undefined;
    if (v < t1) return COLORS.excellent;
    if (v < t2) return COLORS.good;
    if (v < t3) return COLORS.fair;
    return COLORS.poor;
};

const pct = (v: any) => v != null ? `${(v * 100).toFixed(2)}%` : 'N/A';
const dec = (v: any) => v != null ? v.toFixed(3) : 'N/A';

type RowDef = { key: string; label: string; format: (v: any) => string; cellBg?: (v: any) => string | undefined };

const SUMMARY_ROWS: RowDef[] = [
    { key: 'Total Return',      label: 'Total Return',      format: pct, cellBg: v => getColorHigher(v, 0.15, 0.10, 0.05) },
    { key: 'CAGR',              label: 'CAGR',              format: pct, cellBg: v => getColorHigher(v, 0.20, 0.10, 0.07) },
    { key: 'Sharpe',            label: 'Sharpe',            format: dec, cellBg: v => getColorHigher(v, 3.0, 2.0, 1.0) },
    { key: 'Sortino',           label: 'Sortino',           format: dec, cellBg: v => getColorHigher(v, 3.0, 2.0, 1.0) },
    { key: 'Calmar Ratio',      label: 'Calmar',            format: dec, cellBg: v => getColorHigher(v, 3.0, 1.0, 0.5) },
    { key: 'Max Drawdown',      label: 'Max Drawdown',      format: pct, cellBg: v => getColorLower(v != null ? Math.abs(v) : null, 0.10, 0.20, 0.35) },
    { key: 'Daily Vol (ann.)',  label: 'Volatility (ann.)', format: pct, cellBg: v => getColorLower(v, 0.15, 0.20, 0.30) },
    { key: 'Daily Mean (ann.)', label: 'Daily Mean (ann.)', format: pct, cellBg: v => getColorHigher(v, 0.20, 0.10, 0.00) },
    { key: 'Daily Skew',        label: 'Skew',              format: dec, cellBg: v => getColorHigher(v, 1.0, 0.5, 0.0) },
    { key: 'Daily Kurt',        label: 'Kurt',              format: dec, cellBg: v => getColorLower(v, 5, 10, 20) },
    { key: 'Best Day',          label: 'Best Day',          format: pct, cellBg: v => getColorHigher(v, 0.10, 0.05, 0.02) },
    { key: 'Worst Day',         label: 'Worst Day',         format: pct, cellBg: v => getColorHigher(v, -0.02, -0.05, -0.10) },
    { key: 'Start',             label: 'Start',             format: v => v ?? 'N/A' },
    { key: 'End',               label: 'End',               format: v => v ?? 'N/A' },
    { key: 'Risk-free rate',    label: 'Risk-free Rate',    format: v => v ?? 'N/A', cellBg: v => {
        const num = typeof v === 'string' ? parseFloat(v.replace('%', '')) / 100 : v;
        return getColorHigher(isNaN(num) ? null : num, 0.05, 0.035, 0.02);
    }},
];

const TRAILING_ROWS: RowDef[] = [
    { key: 'Return',       label: 'Return',            format: pct, cellBg: v => getColorHigher(v, 0.15, 0.10, 0.05) },
    { key: 'CAGR',         label: 'CAGR',              format: pct, cellBg: v => getColorHigher(v, 0.20, 0.10, 0.07) },
    { key: 'Sharpe',       label: 'Sharpe',            format: dec, cellBg: v => getColorHigher(v, 3.0, 2.0, 1.0) },
    { key: 'Sortino',      label: 'Sortino',           format: dec, cellBg: v => getColorHigher(v, 3.0, 2.0, 1.0) },
    { key: 'Calmar',       label: 'Calmar',            format: dec, cellBg: v => getColorHigher(v, 3.0, 1.0, 0.5) },
    { key: 'Vol (ann.)',   label: 'Volatility (ann.)', format: pct, cellBg: v => getColorLower(v, 0.15, 0.20, 0.30) },
    { key: 'Max Drawdown', label: 'Max Drawdown',      format: pct, cellBg: v => getColorLower(v != null ? Math.abs(v) : null, 0.10, 0.20, 0.35) },
    { key: 'Skew',         label: 'Skew',              format: dec, cellBg: v => getColorHigher(v, 1.0, 0.5, 0.0) },
    { key: 'Kurt',         label: 'Kurt',              format: dec, cellBg: v => getColorLower(v, 5, 10, 20) },
    { key: 'Best Day',     label: 'Best Day',          format: pct, cellBg: v => getColorHigher(v, 0.10, 0.05, 0.02) },
    { key: 'Worst Day',    label: 'Worst Day',         format: pct, cellBg: v => getColorHigher(v, -0.02, -0.05, -0.10) },
    { key: 'Win Rate',     label: 'Win Rate',          format: pct, cellBg: v => getColorHigher(v, 0.65, 0.50, 0.35) },
];

const FREE_TIER_SUMMARY_KEYS = new Set(['Total Return', 'CAGR', 'Max Drawdown']);
const FREE_TIER_TRAILING_KEYS = new Set(['Return', 'CAGR', 'Max Drawdown']);

const LockedCell: React.FC<{ value: string }> = ({ value }) => (
    <Tooltip title="Upgrade to unlock">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Box component="span" sx={{ filter: 'blur(4px)', userSelect: 'none' }}>{value}</Box>
            <LockOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
        </Box>
    </Tooltip>
);

interface SummaryStatsTableProps {
    backtestResult: TradingBacktestResult;
}

export const SummaryStatsTable: React.FC<SummaryStatsTableProps> = ({ backtestResult }) => {
    const summary = backtestResult.summary as Record<string, any>;
    const trailing = backtestResult.trailing_stats as Record<string, Record<string, any>>;
    const isFree = useContext(AuthContext).getSubscriptionTier() === SubscriptionTier.free;

    return (
        <Box sx={{ mt: 4, width: '100%', maxWidth: 1600, display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'stretch' }}>
            {/* Full-Period Summary */}
            <Box sx={{ flex: '0 0 auto', minWidth: 280, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Full-Period Summary</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
                    <Table size="small">
                        <TableBody>
                            {SUMMARY_ROWS.map(({ key, label, format, cellBg }) => {
                                const locked = isFree && !FREE_TIER_SUMMARY_KEYS.has(key);
                                return (
                                    <TableRow key={key} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                                        <TableCell align="right" sx={{ backgroundColor: locked ? undefined : cellBg?.(summary[key]) }}>
                                            {locked ? <LockedCell value={format(summary[key])} /> : format(summary[key])}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Trailing Period Statistics */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Trailing Period Statistics</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                                {TRAILING_PERIODS.map(p => (
                                    <TableCell key={p} align="right" sx={{ fontWeight: 700 }}>{p}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {TRAILING_ROWS.map(({ key, label, format, cellBg }) => {
                                const locked = isFree && !FREE_TIER_TRAILING_KEYS.has(key);
                                return (
                                    <TableRow key={key} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                                        {TRAILING_PERIODS.map(period => {
                                            const periodData = trailing?.[period] as Record<string, any> | undefined;
                                            const rawVal = periodData?.[key];
                                            const formatted = periodData ? format(rawVal) : 'N/A';
                                            return (
                                                <TableCell key={period} align="right" sx={{ backgroundColor: locked ? undefined : cellBg?.(rawVal) }}>
                                                    {locked ? <LockedCell value={formatted} /> : formatted}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};
